import type {
  AdminSettings,
  AiStatus,
  AnalyticsResponse,
  ApiErrorBody,
  AttachmentsStatus,
  Conversation,
  ConversationStatus,
  CustomerContext,
  CannedResponse,
  CannedResponseInput,
  EmailStatus,
  Faq,
  FaqInput,
  KnowledgeSource,
  Message,
  Stats,
  Store,
  WhatsAppStatusResponse,
} from '../types';

/**
 * API client for the embedded admin.
 *
 * Every request carries a fresh Shopify session token in the
 * `Authorization: Bearer <jwt>` header. The token is short-lived (~60s),
 * so we fetch a fresh one for every call rather than caching.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, body: ApiErrorBody) {
    super(body.error?.message ?? `Request failed with status ${status}`);
    this.status = status;
    this.code = body.error?.code ?? 'UNKNOWN';
    this.details = body.error?.details;
  }
}

async function authHeaders(): Promise<Headers> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const bridge = window.shopify;
  if (bridge?.idToken) {
    try {
      const token = await bridge.idToken();
      headers.set('Authorization', `Bearer ${token}`);
    } catch {
      // App Bridge unavailable (likely viewing app outside Shopify admin).
      // Let the request go through unauthenticated — backend will 401 and
      // the UI surfaces a helpful message.
    }
  }
  return headers;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    headers: await authHeaders(),
    credentials: 'include',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const response = await fetch(path, init);

  // 204 No Content
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const errorBody: ApiErrorBody =
      data && typeof data === 'object' && 'error' in data
        ? (data as ApiErrorBody)
        : { error: { code: 'UNKNOWN', message: response.statusText } };
    throw new ApiError(response.status, errorBody);
  }

  return data as T;
}

// ──────────────────────────────────────────────────────────────────────────────
// Endpoints
// ──────────────────────────────────────────────────────────────────────────────

export const api = {
  me: () => request<{ store: Store }>('GET', '/api/admin/me'),

  stats: () => request<Stats>('GET', '/api/admin/stats'),

  analytics: () => request<AnalyticsResponse>('GET', '/api/admin/analytics'),

  getSettings: () =>
    request<{ settings: AdminSettings; ai: AiStatus; email: EmailStatus; attachments?: AttachmentsStatus }>(
      'GET',
      '/api/admin/settings',
    ),

  updateSettings: (settings: AdminSettings) =>
    request<{ settings: AdminSettings }>('PUT', '/api/admin/settings', settings),

  listKnowledgeSources: () =>
    request<{ sources: KnowledgeSource[] }>('GET', '/api/admin/knowledge-sources'),

  createKnowledgeSource: (url: string) =>
    request<{ source: KnowledgeSource }>('POST', '/api/admin/knowledge-sources', { url }),

  refreshKnowledgeSource: (sourceId: string) =>
    request<{ source: KnowledgeSource }>(
      'POST',
      `/api/admin/knowledge-sources/${sourceId}/refresh`,
    ),

  deleteKnowledgeSource: (sourceId: string) =>
    request<void>('DELETE', `/api/admin/knowledge-sources/${sourceId}`),

  listFaqs: () => request<{ faqs: Faq[] }>('GET', '/api/admin/faqs'),

  createFaq: (input: FaqInput) => request<{ faq: Faq }>('POST', '/api/admin/faqs', input),

  updateFaq: (faqId: string, input: FaqInput) =>
    request<{ faq: Faq }>('PUT', `/api/admin/faqs/${faqId}`, input),

  deleteFaq: (faqId: string) => request<void>('DELETE', `/api/admin/faqs/${faqId}`),

  listCannedResponses: () =>
    request<{ cannedResponses: CannedResponse[] }>('GET', '/api/admin/canned-responses'),

  createCannedResponse: (input: CannedResponseInput) =>
    request<{ cannedResponse: CannedResponse }>('POST', '/api/admin/canned-responses', input),

  updateCannedResponse: (id: string, input: CannedResponseInput) =>
    request<{ cannedResponse: CannedResponse }>('PUT', `/api/admin/canned-responses/${id}`, input),

  deleteCannedResponse: (id: string) =>
    request<void>('DELETE', `/api/admin/canned-responses/${id}`),

  listConversations: (params: { status?: ConversationStatus; take?: number; skip?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.take !== undefined) search.set('take', String(params.take));
    if (params.skip !== undefined) search.set('skip', String(params.skip));
    const qs = search.toString();
    return request<{ count: number; conversations: Conversation[] }>(
      'GET',
      `/api/admin/conversations${qs ? `?${qs}` : ''}`,
    );
  },

  getConversation: (id: string) =>
    request<{ conversation: Conversation }>('GET', `/api/admin/conversations/${id}`),

  getCustomerContext: (conversationId: string) =>
    request<CustomerContext>(
      'GET',
      `/api/admin/conversations/${conversationId}/customer-context`,
    ),

  sendAdminMessage: (conversationId: string, content: string) =>
    request<{ message: Message }>(
      'POST',
      `/api/admin/conversations/${conversationId}/messages`,
      { content },
    ),

  sendAdminMessageWithFiles: async (
    conversationId: string,
    content: string,
    files: File[],
  ): Promise<{ message: Message }> => {
    const headers = await authHeaders();
    headers.delete('Content-Type'); // let the browser set the multipart boundary
    const form = new FormData();
    if (content) form.set('content', content);
    for (const f of files) form.append('files', f);
    const response = await fetch(`/api/admin/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
      const errorBody: ApiErrorBody =
        data && typeof data === 'object' && 'error' in data
          ? (data as ApiErrorBody)
          : { error: { code: 'UNKNOWN', message: response.statusText } };
      throw new ApiError(response.status, errorBody);
    }
    return data as { message: Message };
  },

  setConversationStatus: (conversationId: string, status: ConversationStatus) =>
    request<{ conversation: Conversation }>(
      'PATCH',
      `/api/admin/conversations/${conversationId}/status`,
      { status },
    ),

  startBilling: () => request<{ confirmationUrl: string }>('POST', '/api/admin/billing/start'),

  cancelBilling: () =>
    request<{ plan: Store['plan']; activeSubscriptionCount: number }>(
      'POST',
      '/api/admin/billing/cancel',
    ),

  whatsappStatus: () =>
    request<WhatsAppStatusResponse>('GET', '/api/admin/whatsapp/status'),

  whatsappConnect: (phone: string) =>
    request<{ phone: string }>('POST', '/api/admin/whatsapp/connect', { phone }),

  whatsappVerify: (code: string) =>
    request<void>('POST', '/api/admin/whatsapp/verify', { code }),

  whatsappDisconnect: () => request<void>('DELETE', '/api/admin/whatsapp'),
};
